/* RojaFume theme — storefront behaviour.
   Talks to Shopify's Ajax API (/cart/*.js, /products/<handle>.js, /search/suggest.json);
   routes come from window.RF, set in layout/theme.liquid. */
(() => {
  "use strict";

  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => [...el.querySelectorAll(s)];
  const RF = window.RF || { routes: {}, moneyFormat: "₹{{amount}}", freeShipping: 0 };
  const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const esc = s => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  /* ---------- money ---------- */

  function formatMoney(cents) {
    const n = Number(cents) / 100;
    const fmt = (dec, thou, sep) => {
      const [i, f] = n.toFixed(dec).split(".");
      const int = i.replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return f ? int + sep + f : int;
    };
    const indian = v => {
      const [i, f] = n.toFixed(v).split(".");
      const last3 = i.slice(-3), rest = i.slice(0, -3);
      const int = (rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," : "") + last3;
      return f ? int + "." + f : int;
    };
    return RF.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => ({
      amount: indian(2),
      amount_no_decimals: indian(0),
      amount_with_comma_separator: fmt(2, ".", ","),
      amount_no_decimals_with_comma_separator: fmt(0, ".", ","),
      amount_with_apostrophe_separator: fmt(2, "'", "."),
    }[key] ?? indian(2)));
  }

  const imgUrl = (src, width) => {
    if (!src) return "";
    try {
      const u = new URL(src, location.origin);
      u.searchParams.set("width", width);
      return u.toString();
    } catch { return src; }
  };

  /* ---------- toast ---------- */

  let toastTimer;
  function toast(msg) {
    const t = $("#toast");
    if (!t) return;
    t.textContent = msg;
    t.classList.add("is-on");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove("is-on"), 3200);
  }

  /* ---------- overlays (drawers, modal, search) ---------- */

  let openEl = null, lastFocus = null;
  const scrim = $("#scrim");

  function openLayer(el, focusSel) {
    if (!el) return;
    if (openEl && openEl !== el) closeLayer(false);
    lastFocus = document.activeElement;
    openEl = el;
    el.classList.add("is-open");
    el.setAttribute("aria-hidden", "false");
    scrim && scrim.classList.add("is-on");
    document.body.classList.add("is-locked");
    if (el.id === "mobileMenu") $("#menuOpen")?.setAttribute("aria-expanded", "true");
    const target = focusSel ? $(focusSel, el) : $("button, a, input", el);
    setTimeout(() => target && target.focus({ preventScroll: true }), 60);
  }

  function closeLayer(restore = true) {
    if (!openEl) return;
    openEl.classList.remove("is-open");
    openEl.setAttribute("aria-hidden", "true");
    if (openEl.id === "mobileMenu") $("#menuOpen")?.setAttribute("aria-expanded", "false");
    openEl = null;
    scrim && scrim.classList.remove("is-on");
    document.body.classList.remove("is-locked");
    if (restore && lastFocus) lastFocus.focus({ preventScroll: true });
  }

  scrim && scrim.addEventListener("click", () => closeLayer());
  document.addEventListener("keydown", e => {
    if (e.key === "Escape") closeLayer();
    if (e.key === "Tab" && openEl) {
      const f = $$("button:not([disabled]), a[href], input:not([type=hidden]), select, textarea", openEl).filter(n => n.offsetParent);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f.at(-1).focus(); }
      else if (!e.shiftKey && document.activeElement === f.at(-1)) { e.preventDefault(); f[0].focus(); }
    }
  });
  document.addEventListener("click", e => {
    const c = e.target.closest("[data-close]");
    if (c && openEl && openEl.contains(c)) { e.preventDefault(); closeLayer(); }
  });

  $("#menuOpen")?.addEventListener("click", () => openLayer($("#mobileMenu")));
  $("#searchOpen")?.addEventListener("click", () => openLayer($("#search"), "#searchInput"));
  $("#cartOpen")?.addEventListener("click", e => {
    if (RF.template === "cart") return;
    e.preventDefault();
    refreshCart().then(() => openLayer($("#cart")));
  });

  // mega menu opens on hover (CSS) and on click/tap of its toggle (touch laptops, keyboards);
  // clicking outside, pressing Escape or coming back to the page closes it
  $$(".nav__item--mega").forEach(item => {
    const btn = $(".nav__toggle", item) || $("button.nav__link", item);
    if (!btn) return;
    // is-shut keeps it closed under a still-hovering mouse until the mouse leaves
    const close = shut => { item.classList.remove("is-open"); item.classList.toggle("is-shut", !!shut); btn.setAttribute("aria-expanded", "false"); };
    const panel = $(".mega", item);
    let pointer = "";
    btn.addEventListener("pointerdown", e => { pointer = e.pointerType; });
    btn.addEventListener("click", () => {
      // with a mouse or keyboard the panel is already showing (hover / focus), so the click closes it;
      // a tap only goes by our own open state, since touch "hover" sticks around
      const shown = pointer === "touch" ? item.classList.contains("is-open")
        : item.classList.contains("is-open") || getComputedStyle(panel).visibility === "visible";
      pointer = "";
      if (shown) return close(true);
      item.classList.remove("is-shut");
      item.classList.add("is-open");
      btn.setAttribute("aria-expanded", "true");
    });
    item.addEventListener("mouseleave", () => item.classList.remove("is-shut"));
    item.addEventListener("focusout", e => { if (!item.contains(e.relatedTarget)) item.classList.remove("is-shut"); });
    document.addEventListener("click", e => { if (!item.contains(e.target)) close(); });
    document.addEventListener("keydown", e => {
      if (e.key === "Escape" && item.matches(".is-open, :focus-within")) { close(true); btn.focus(); }
    });
    addEventListener("pageshow", e => { if (e.persisted) { close(); document.activeElement?.blur(); } });
  });

  const header = $("#header");
  if (header) addEventListener("scroll", () => header.classList.toggle("is-scrolled", scrollY > 10), { passive: true });

  /* ---------- loader ---------- */

  if (document.body.classList.contains("is-loading")) {
    const start = performance.now();
    const minShow = reduceMotion ? 300 : 1600;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setTimeout(() => {
        document.body.classList.remove("is-loading");
        setTimeout(() => $("#loader")?.remove(), 900);
      }, Math.max(0, minShow - (performance.now() - start)));
    };
    if (document.readyState === "complete") finish(); else addEventListener("load", finish);
    setTimeout(finish, 3500);
  }

  /* ---------- announcement bar ---------- */

  (() => {
    const msgs = $$(".announce__msg");
    if (msgs.length < 2) return;
    let i = 0, timer;
    const go = n => {
      i = (n + msgs.length) % msgs.length;
      msgs.forEach((m, k) => m.classList.toggle("is-active", k === i));
      clearTimeout(timer);
      if (!reduceMotion) timer = setTimeout(() => go(i + 1), 4500);
    };
    $$("[data-announce]").forEach(b => b.addEventListener("click", () => go(i + +b.dataset.announce)));
    go(0);
  })();

  /* ---------- cart ---------- */

  async function cartFetch(url, body) {
    const res = await fetch(url, {
      method: body ? "POST" : "GET",
      headers: body instanceof FormData ? { Accept: "application/json" } : { "Content-Type": "application/json", Accept: "application/json" },
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || "Something went wrong — please try again.");
    return data;
  }

  // Line item properties, e.g. the perfumes picked for a build-your-box line. Keys starting with "_" are private.
  const cartProps = item => {
    const rows = Object.entries(item.properties || {}).filter(([k, v]) => v && !k.startsWith("_"));
    return rows.length ? `<ul class="cart-item__props">${rows.map(([k, v]) => `<li>${esc(k)}: ${esc(v)}</li>`).join("")}</ul>` : "";
  };

  function renderCart(cart) {
    const drawer = $("#cart");
    if (!drawer) return;
    const count = cart.item_count;
    const countEl = $("#cartCount");
    if (countEl) { countEl.textContent = count; countEl.classList.toggle("has-items", count > 0); }
    $("#cartOpen")?.setAttribute("aria-label", `Cart, ${count} item${count === 1 ? "" : "s"}`);
    drawer.classList.toggle("is-empty", count === 0);
    $$("[data-subtotal]").forEach(el => el.textContent = formatMoney(cart.total_price));

    if (RF.freeShipping > 0) {
      const left = RF.freeShipping - cart.total_price;
      $$("[data-ship-text]", drawer).forEach(el => el.innerHTML = left > 0
        ? `You're <strong>${formatMoney(left)}</strong> away from free delivery`
        : "<strong>You've unlocked free delivery</strong>");
      $$("[data-ship-bar]", drawer).forEach(el => el.style.width = Math.min(100, cart.total_price / RF.freeShipping * 100) + "%");
    }

    $("#cartItems").innerHTML = cart.items.map((item, k) => `
      <li class="cart-item">
        <a class="cart-item__img" href="${esc(item.url)}">${item.image ? `<img src="${esc(imgUrl(item.image, 200))}" alt="${esc(item.product_title)}" loading="lazy">` : ""}</a>
        <div>
          <a class="cart-item__name" href="${esc(item.url)}">${esc(item.product_title)}</a>
          ${item.product_has_only_default_variant ? "" : `<p class="cart-item__meta">${esc(item.variant_title)}</p>`}
          ${cartProps(item)}
          <div class="qty">
            <button type="button" data-line="${k + 1}" data-qty="${item.quantity - 1}" aria-label="Decrease ${esc(item.product_title)}">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-line="${k + 1}" data-qty="${item.quantity + 1}" aria-label="Increase ${esc(item.product_title)}">+</button>
          </div>
        </div>
        <div>
          <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
          <button class="cart-item__remove" type="button" data-line="${k + 1}" data-qty="0">Remove</button>
        </div>
      </li>`).join("");
  }

  const refreshCart = () => cartFetch(RF.routes.cart + ".js").then(c => { renderCart(c); return c; }).catch(() => {});

  async function addToCart(formData) {
    await cartFetch(RF.routes.cartAdd + ".js", formData);
    const cart = await refreshCart();
    const btn = $("#cartOpen");
    if (btn) { btn.classList.remove("bump"); void btn.offsetWidth; btn.classList.add("bump"); }
    return cart;
  }

  $("#cartItems")?.addEventListener("click", async e => {
    const b = e.target.closest("[data-line]");
    if (!b) return;
    b.disabled = true;
    try {
      const cart = await cartFetch(RF.routes.cartChange + ".js", { line: +b.dataset.line, quantity: +b.dataset.qty });
      renderCart(cart);
      ($(`#cartItems [data-line="${b.dataset.line}"]`) || $("#cart [data-close]"))?.focus();
    } catch (err) { toast(err.message); b.disabled = false; }
  });

  // product forms (product page) add through Ajax and open the drawer
  $$("form[data-product-form]").forEach(form => form.addEventListener("submit", async e => {
    e.preventDefault();
    const btn = $("[data-add-button]", form);
    const err = $("[data-form-error]", form);
    if (err) err.textContent = "";
    btn && (btn.disabled = true);
    try {
      await addToCart(new FormData(form));
      openLayer($("#cart"));
    } catch (ex) {
      if (err) err.textContent = ex.message; else toast(ex.message);
    } finally { btn && (btn.disabled = false); syncVariant && syncVariant(); }
  }));

  // Checkout runs through GoKwik (payments by Razorpay): snippets/gokwik.liquid loads its script, which
  // defines onCheckoutClick(button). It reads /cart.js itself and shows the button's last child element
  // (.gk-loader) as a spinner — without that element it fails silently. Until GoKwik has loaded, or if it
  // fails to, buttons fall back to Shopify's own checkout.
  RF.checkout = btn => {
    if (typeof window.onCheckoutClick === "function" && btn.lastElementChild) window.onCheckoutClick(btn);
    else location.href = "/checkout";
  };
  // GoKwik's Buy now (also used for build-your-box Checkout) swaps the cart: it saves the shopper's cart
  // in localStorage "cartItems", empties it, adds only the bought item, and puts the cart back when its
  // window fires "modal_closed". A shopper who leaves the page with that window still open never gets
  // their cart back, so on the next page load finish the job the same way GoKwik does.
  // GoKwik keeps both values in localStorage and in cookies of the same names; both are cleared, or a
  // leftover "gkbuynow" would make GoKwik empty the cart the next time any checkout window closes.
  (async () => {
    const cookie = name => decodeURIComponent((document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)")) || [])[1] || "");
    let saved, active;
    try {
      active = localStorage.getItem("gkbuynow") === "active" || cookie("gkbuynow") === "active";
      saved = JSON.parse(localStorage.getItem("cartItems") || cookie("cartItems") || "null");
    } catch { return; }
    if (!active) return;
    try {
      await cartFetch(RF.routes.root.replace(/\/?$/, "/") + "cart/clear.js", {});
      if (Array.isArray(saved) && saved.length) await cartFetch(RF.routes.cartAdd + ".js", { items: saved });
      ["gkbuynow", "cartItems"].forEach(k => { localStorage.removeItem(k); document.cookie = `${k}=; path=/; max-age=0`; });
      refreshCart();
    } catch {}
  })();

  // "Buy now" on product pages: GoKwik checks out just this product (read from #product-form, see
  // main-product.liquid). Without GoKwik it adds the product to the cart and goes to Shopify's checkout.
  RF.buyNow = async btn => {
    if (typeof window.onBuyNowClick === "function") { window.onBuyNowClick(btn); return; }
    const form = btn.closest("form");
    try { await cartFetch(RF.routes.cartAdd + ".js", new FormData(form)); location.href = "/checkout"; }
    catch (err) { const el = $("[data-form-error]", form); if (el) el.textContent = err.message; }
  };
  document.addEventListener("click", e => {
    const buy = e.target.closest("[data-buy-now]");
    if (buy) { e.preventDefault(); if (!buy.disabled) RF.buyNow(buy); return; }
    const btn = e.target.closest("[data-checkout-btn]");
    if (!btn) return;
    e.preventDefault();
    RF.checkout(btn);
  });

  // cart page: quantity inputs and note save automatically
  const cartForm = $("[data-cart-page]");
  if (cartForm) {
    let t;
    $$("[data-cart-qty]", cartForm).forEach(input => input.addEventListener("change", () => cartForm.submit()));
    $("#cartNote")?.addEventListener("input", e => {
      clearTimeout(t);
      t = setTimeout(() => cartFetch(RF.routes.cartUpdate + ".js", { note: e.target.value }).catch(() => {}), 500);
    });
  }

  /* ---------- quick view ---------- */

  const qv = { product: null, variant: null, qty: 1 };

  function qvVariant() {
    const sel = $$("#qvOptions .sizes").map(g => $("input:checked", g)?.value);
    return qv.product.variants.find(v => v.options.every((o, i) => o === sel[i])) || qv.product.variants[0];
  }

  function qvUpdate() {
    qv.variant = qvVariant();
    const v = qv.variant;
    $("#qvPrice").innerHTML = (v.compare_at_price > v.price ? `<s class="price__was">${formatMoney(v.compare_at_price)}</s> ` : "") + formatMoney(v.price);
    $("#qvQty").textContent = qv.qty;
    const add = $("#qvAdd");
    add.disabled = !v.available;
    add.textContent = v.available ? `Add to cart · ${formatMoney(v.price * qv.qty)}` : "Sold out";
    const img = v.featured_image?.src || qv.product.featured_image;
    if (img) $("#qvMedia").innerHTML = `<img src="${esc(imgUrl(img, 900))}" alt="${esc(qv.product.title)}">`;
    $$("#qvOptions .size").forEach(l => l.classList.toggle("is-checked", $("input", l).checked));
  }

  async function openQuickView(handle) {
    try {
      const res = await fetch(`${RF.routes.root.replace(/\/$/, "")}/products/${encodeURIComponent(handle)}.js`);
      if (!res.ok) throw new Error();
      const p = await res.json();
      Object.assign(qv, { product: p, qty: 1 });
      $("#qvFamily").textContent = p.type || "";
      $("#qvName").textContent = p.title;
      const tmp = document.createElement("div");
      tmp.innerHTML = p.description || "";
      const text = tmp.textContent.trim();
      $("#qvDesc").textContent = text.length > 240 ? text.slice(0, 237).trim() + "…" : text;
      $("#qvMore").href = p.url;
      $("#qvError").textContent = "";
      const onlyDefault = p.variants.length === 1 && p.options.length === 1 && p.variants[0].title === "Default Title";
      // an option with a single value isn't a choice: keep it (for the variant lookup) but hide it
      $("#qvOptions").innerHTML = onlyDefault ? "" : p.options.map((o, i) => `<div class="opt"${o.values.length < 2 ? " hidden" : ""}>
        <p class="qv__label">${esc(o.name)}</p>
        <div class="sizes" role="radiogroup" aria-label="${esc(o.name)}">
          ${o.values.map((val, k) => `
            <label class="size"><input class="sr-only" type="radio" name="qv-opt-${i}" value="${esc(val)}"${k === 0 ? " checked" : ""}><span>${esc(val)}</span></label>`).join("")}
        </div></div>`).join("");
      const firstAvail = p.variants.find(v => v.available) || p.variants[0];
      firstAvail.options.forEach((val, i) => {
        const r = $$(`#qvOptions input[name="qv-opt-${i}"]`).find(x => x.value === val);
        if (r) r.checked = true;
      });
      qvUpdate();
      openLayer($("#quickView"), ".modal__close");
    } catch {
      toast("Couldn't load that product — please try again.");
    }
  }

  $("#qvOptions")?.addEventListener("change", qvUpdate);
  $$("[data-qv-qty]").forEach(b => b.addEventListener("click", () => {
    qv.qty = Math.max(1, Math.min(10, qv.qty + +b.dataset.qvQty));
    qvUpdate();
  }));
  $("#qvAdd")?.addEventListener("click", async () => {
    const fd = new FormData();
    fd.append("id", qv.variant.id);
    fd.append("quantity", qv.qty);
    $("#qvAdd").disabled = true;
    try {
      await addToCart(fd);
      closeLayer(false);
      setTimeout(() => openLayer($("#cart")), 200);
    } catch (ex) {
      $("#qvError").textContent = ex.message;
      $("#qvAdd").disabled = false;
    }
  });
  document.addEventListener("click", e => {
    const t = e.target.closest("[data-quick]");
    if (!t) return;
    e.preventDefault();
    openQuickView(t.dataset.quick);
  });

  /* ---------- bundle offers: step-by-step builder ---------- */

  // sections/bundle-offers.liquid renders the cards and a JSON blob of every bundle's steps and products.
  // Choosing a bundle opens the builder: one step per pick, each showing only the perfumes that step allows
  // (and greying out ones that would break the bundle's "at most N from …" limit), then a review and
  // "Add bundle to cart", which adds the bundle product with the picks as line item properties.
  $$("[data-bundles]").forEach(root => {
    let data;
    try { data = JSON.parse($("[data-bundle-data]", root).textContent); } catch { return; }
    const cards = $("[data-bundle-cards]", root), ui = $("[data-builder]", root);
    const el = name => $(`[data-b-${name}]`, ui);
    const P = data.products;
    const money = c => formatMoney(c).replace(/[.,]00(?!\d)/, "");  // ₹1,499 rather than ₹1,499.00
    let bundle = null, picks = [], step = 0;

    const capCount = (list, skip = -1) => list.filter((id, i) => i !== skip && id && bundle.cap.includes(+id)).length;
    // a product is offered at a step if the step lists it and it's in stock; the limit greys it out
    const allowed = (id, at) => {
      const p = P[id];
      if (!p || !p.available) return { ok: false, why: "Sold out" };
      if (bundle.capMax > 0 && bundle.cap.includes(+id) && capCount(picks, at) >= bundle.capMax)
        return { ok: false, why: `Max ${bundle.capMax} from ${bundle.capLabel || "these"}` };
      return { ok: true };
    };

    function render() {
      const n = bundle.steps.length, done = picks.filter(Boolean).length, reviewing = step >= n;
      el("offer").textContent = bundle.offer;
      el("title").textContent = bundle.title;
      el("price").innerHTML = `<b>${money(bundle.price)}</b>`;
      el("stepper").innerHTML = bundle.steps.map((s, i) =>
        `<li class="${i === step ? "is-on" : ""}${picks[i] ? " is-done" : ""}"><button type="button" data-goto="${i}"><span>${i + 1}</span>Step ${i + 1}</button></li>`).join("")
        + `<li class="${reviewing ? "is-on" : ""}"><button type="button" data-goto="${n}"${done < n ? " disabled" : ""}><span>✓</span>Review</button></li>`;
      el("tray").innerHTML = bundle.steps.map((s, i) => {
        const p = P[picks[i]];
        return `<button type="button" class="bob__slot${p ? " is-filled" : ""}${i === step ? " is-on" : ""}" data-goto="${i}" aria-label="Step ${i + 1}${p ? ": " + esc(p.title) : ""}">
          ${p && p.img ? `<img src="${esc(imgUrl(p.img, 160))}" alt="">` : `<span>${i + 1}</span>`}</button>`;
      }).join("");

      el("grid").hidden = reviewing; el("review").hidden = !reviewing;
      if (!reviewing) {
        const s = bundle.steps[step];
        el("prompt").innerHTML = `<b>Step ${step + 1} of ${n}</b> Pick ${n > 1 ? "your " + ordinal(step + 1) + " perfume" : "your perfume"} from <em>${esc(s.label)}</em>`;
        const capped = bundle.capMax > 0;
        el("rule").hidden = !capped;
        if (capped) el("rule").textContent = `Up to ${bundle.capMax} from ${bundle.capLabel || "the limited collections"} · ${capCount(picks, step)} chosen`;
        const ids = [...new Set(s.ids.filter(id => id && P[id]))].sort((a, b) => (P[b].available - P[a].available));
        el("grid").innerHTML = ids.map(id => {
          const p = P[id], a = allowed(id, step), on = String(picks[step]) === String(id);
          return `<button type="button" class="bob__pick${on ? " is-on" : ""}" data-pick="${id}"${a.ok ? "" : " disabled"}>
            <span class="bob__img">${p.img ? `<img src="${esc(imgUrl(p.img, 400))}" alt="" loading="lazy">` : ""}${a.ok ? "" : `<em>${esc(a.why)}</em>`}</span>
            ${p.family ? `<span class="bob__fam">${esc(p.family)}</span>` : ""}
            <span class="bob__name">${esc(p.title)}</span>
            <span class="bob__was">${money(p.price)}</span>
          </button>`;
        }).join("") || `<p class="bob__none">Nothing in stock for this step right now.</p>`;
      } else {
        el("prompt").innerHTML = `<b>Your bundle</b>`;
        el("rule").hidden = true;
        const worth = bundle.worth || picks.reduce((t, id) => t + P[id].price, 0), save = worth - bundle.price;
        el("review").innerHTML = `<ul class="bob__list">${picks.map((id, i) => `<li><span>${i + 1}</span>${esc(P[id].title)}<s>${money(P[id].price)}</s><button type="button" data-goto="${i}">Change</button></li>`).join("")}</ul>
          <p class="bob__total">Bundle price <b>${money(bundle.price)}</b>${save > 0 ? ` <s>${money(worth)}</s> <em>You save ${money(save)}</em>` : ""}</p>
          ${bundle.variant ? `<button class="btn btn--gold bob__add" type="button" data-add>${esc(data.addLabel || "Add bundle to cart")}</button>`
            : `<p class="bob__none">This bundle can't be bought yet: its bundle product isn't set up.</p>`}`;
      }
    }
    const ordinal = n => ["first", "second", "third", "fourth", "fifth", "sixth"][n - 1] || n + "th";

    function open(id, push = true) {
      bundle = data.bundles.find(b => b.id === id);
      if (!bundle) return;
      picks = new Array(bundle.steps.length).fill(null); step = 0;
      cards.hidden = true; ui.hidden = false;
      render();
      if (push) history.replaceState(null, "", "#bundle-" + id);
      ui.scrollIntoView({ block: "start" });
    }
    function close() {
      ui.hidden = true; cards.hidden = false; bundle = null;
      history.replaceState(null, "", location.pathname + location.search);
      cards.scrollIntoView({ block: "start" });
    }

    root.addEventListener("click", async e => {
      const o = e.target.closest("[data-open-bundle]");
      if (o) { e.preventDefault(); return open(o.dataset.openBundle); }
      if (!bundle) return;
      if (e.target.closest("[data-back]")) return close();
      const g = e.target.closest("[data-goto]");
      if (g && !g.disabled) { step = +g.dataset.goto; return render(); }
      const pk = e.target.closest("[data-pick]");
      if (pk && !pk.disabled) {
        picks[step] = pk.dataset.pick;
        // move on to the next empty step, or the review
        const next = picks.findIndex(x => !x);
        step = next === -1 ? bundle.steps.length : next;
        render();
        el("prompt").scrollIntoView({ block: "nearest", behavior: reduceMotion ? "auto" : "smooth" });
        return;
      }
      const add = e.target.closest("[data-add]");
      if (add) {
        add.disabled = true;
        const properties = { _Bundle: bundle.title };
        picks.forEach((id, i) => { properties[`Perfume ${i + 1}`] = P[id].title; });
        properties["_Box items"] = picks.map(id => P[id].variant).join(",");
        try {
          await addToCart({ items: [{ id: bundle.variant, quantity: 1, properties }] });
          openLayer($("#cart"));
          close();
        } catch (ex) { toast(ex.message); add.disabled = false; }
      }
    });

    const m = location.hash.match(/^#bundle-(.+)$/);
    if (m) open(m[1], false);
  });

  /* ---------- launch offer countdowns ---------- */

  // Each [data-countdown="<ISO end time>"] ticks every second; at zero its [data-offer] wrapper goes away
  // and anything marked [data-after-offer] (the normal announcement bar) comes back.
  const countdowns = $$("[data-countdown]");
  if (countdowns.length) {
    const pad = n => String(n).padStart(2, "0");
    const tick = () => {
      const now = Date.now();
      let ended = false;
      countdowns.forEach(el => {
        const left = Math.max(0, Math.floor((Date.parse(el.dataset.countdown) - now) / 1000));
        if (!left) { ended = true; const box = el.closest("[data-offer]"); if (box) box.hidden = true; return; }
        const parts = { d: Math.floor(left / 86400), h: pad(Math.floor(left % 86400 / 3600)), m: pad(Math.floor(left % 3600 / 60)), s: pad(left % 60) };
        $$("[data-cd]", el).forEach(b => { b.textContent = parts[b.dataset.cd]; });
      });
      if (ended) { $$("[data-after-offer]").forEach(el => { el.hidden = false; }); }
      return ended;
    };
    if (!tick()) { const t = setInterval(() => { if (tick()) clearInterval(t); }, 1000); }
  }

  /* ---------- shop the moment: tabs ---------- */

  $$("[data-moment]").forEach(root => {
    const tabs = $$("[role=tab]", root);
    const show = tab => {
      tabs.forEach(t => {
        const on = t === tab;
        t.setAttribute("aria-selected", on);
        t.tabIndex = on ? 0 : -1;
        const panel = $("#" + t.getAttribute("aria-controls"), root);
        if (panel) panel.hidden = !on;
      });
      dispatchEvent(new Event("resize"));  // carousels in a just-shown panel recheck their arrows
    };
    tabs.forEach((t, i) => {
      t.addEventListener("click", () => show(t));
      t.addEventListener("keydown", e => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
        if (!step) return;
        const next = tabs[(i + step + tabs.length) % tabs.length];
        show(next); next.focus();
      });
    });
    // theme editor: selecting a tab block shows its panel
    root.addEventListener("shopify:block:select", e => { const t = tabs.find(x => x.id === "mt-" + e.detail.blockId); if (t) show(t); });
  });

  /* ---------- shoppable reels ---------- */

  $$("[data-reels]").forEach(root => {
    const data = $$(".reel", root).map(c => { try { return JSON.parse($("[data-reel]", c).textContent); } catch { return {}; } });

    // row previews load and play (silently) only while they're on screen
    const previews = $$(".reel__media video", root);
    if (previews.length && !reduceMotion && "IntersectionObserver" in window) {
      const io = new IntersectionObserver(entries => entries.forEach(e => {
        const v = e.target;
        if (e.isIntersecting) { if (!v.getAttribute("src")) v.src = v.dataset.src; v.play().catch(() => {}); }
        else { v.pause(); setRowSound(v, false, true); }  // a reel scrolled away goes quiet again
      }), { threshold: 0.6 });
      previews.forEach(v => io.observe(v));
    }

    // sound: everything starts muted; turning it on fades the volume up rather than jumping
    // a plain timer rather than animation frames, which browsers pause in background tabs
    function fadeTo(v, to, ms = 600) {
      return new Promise(done => {
        clearInterval(v._fade);
        const from = v.volume, t0 = Date.now();
        v._fade = setInterval(() => {
          const k = Math.min(1, (Date.now() - t0) / ms);
          v.volume = from + (to - from) * k;
          if (k === 1) { clearInterval(v._fade); done(); }
        }, 30);
      });
    }
    function setRowSound(v, on, instant) {
      const b = $("[data-reel-sound]", v.closest(".reel__frame"));
      if (b) {
        b.setAttribute("aria-pressed", String(on));
        b.setAttribute("aria-label", on ? "Turn sound off" : "Turn sound on");
        $("[data-icon-muted]", b).hidden = on;
        $("[data-icon-sound]", b).hidden = !on;
      }
      if (on) { v.volume = 0; v.muted = false; fadeTo(v, 1); }
      else if (instant) { clearInterval(v._fade); v.muted = true; }
      else if (!v.muted) fadeTo(v, 0, 250).then(() => { v.muted = true; });
    }
    root.addEventListener("click", e => {
      const b = e.target.closest("[data-reel-sound]");
      if (!b) return;
      const v = $("video", b.closest(".reel__frame"));
      const on = b.getAttribute("aria-pressed") !== "true";
      previews.forEach(o => { if (o !== v) setRowSound(o, false); });  // one reel speaks at a time
      if (on) { if (!v.getAttribute("src")) v.src = v.dataset.src; v.play().catch(() => {}); }
      setRowSound(v, on);
    });

    // Instagram reels in the row: load the embed near the screen and scale it so only the
    // video shows. Inside a frame, Instagram's embed is 400px wide with a 54px header, then
    // the video at the post's own shape: 400 tall for square posts, up to 500 for 4:5 reels.
    // Scaling for 400 fills the card either way — a taller video just loses a little of its
    // bottom, instead of Instagram's footer showing in the card.
    const igFrames = $$(".reel__ig", root);
    if (igFrames.length) {
      const fit = f => { f.style.setProperty("--s", f.parentElement.clientHeight / 400); };
      const ro = "ResizeObserver" in window ? new ResizeObserver(es => es.forEach(e => fit($(".reel__ig", e.target)))) : null;
      const load = f => {
        if (f.src) return;
        f.addEventListener("load", () => f.classList.add("is-loaded"), { once: true });
        f.src = f.dataset.src;
      };
      const io = "IntersectionObserver" in window
        ? new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { load(e.target); io.unobserve(e.target); } }), { rootMargin: "300px" })
        : null;
      igFrames.forEach(f => { fit(f); ro?.observe(f.parentElement); io ? io.observe(f) : load(f); });
    }

    const add = async (btn, variant) => {
      const label = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Adding…";
      try { await addToCart({ id: variant, quantity: 1 }); openLayer($("#cart")); }
      catch (err) { toast(err.message); }
      btn.disabled = false;
      btn.textContent = label;
    };
    root.addEventListener("click", e => {
      const b = e.target.closest("[data-reel-add]");
      if (b) add(b, +b.dataset.reelAdd);
    });

    // full-screen player — moved to <body> so no section styling can clip it
    const viewer = $("[data-reel-viewer]", root);
    if (!viewer) return;
    document.body.appendChild(viewer);
    const q = s => $(s, viewer);
    const video = q("[data-reelv-video]"), still = q("[data-reelv-still]"), muteBtn = q("[data-reelv-mute]");
    const igBox = q("[data-reelv-ig]");
    // Instagram's player lives in an iframe; emptying the box is the only way to stop its sound
    const clearIg = () => { igBox.innerHTML = ""; igBox.hidden = true; viewer.classList.remove("is-ig"); };
    let i = 0, muted = true;

    const syncMute = () => {
      if (!muted && video.muted) { video.volume = 0; video.muted = false; fadeTo(video, 1); }
      else video.muted = muted;
      muteBtn.setAttribute("aria-pressed", String(!muted));
      muteBtn.setAttribute("aria-label", muted ? "Unmute" : "Mute");
      q("[data-icon-muted]").hidden = !muted;
      q("[data-icon-sound]").hidden = muted;
    };

    const show = k => {
      i = (k + data.length) % data.length;
      const d = data[i];
      clearIg();
      if (!d.video && d.ig) {
        video.pause();
        video.removeAttribute("src");
        video.hidden = true;
        still.hidden = true;
        igBox.hidden = false;
        viewer.classList.add("is-ig");
        const f = document.createElement("iframe");
        f.src = d.ig;
        f.title = d.title ? `${d.title} on Instagram` : "Instagram reel";
        f.allow = "autoplay; encrypted-media; picture-in-picture; clipboard-write";
        f.setAttribute("scrolling", "no");
        igBox.appendChild(f);
      } else if (d.video) {
        still.hidden = true;
        video.hidden = false;
        video.poster = d.poster || "";
        video.src = d.video;
        syncMute();
        // browsers may refuse to start with sound; fall back to silent rather than not playing
        video.play().catch(() => { muted = true; syncMute(); video.play().catch(() => {}); });
      } else {
        video.pause();
        video.removeAttribute("src");
        video.hidden = true;
        still.hidden = false;
        still.src = d.poster || "";
      }
      muteBtn.hidden = !d.video;
      q("[data-reelv-cap]").textContent = d.caption || "";

      const prod = q("[data-reelv-prod]");
      prod.hidden = !d.title;
      if (!d.title) return;
      q("[data-reelv-name]").textContent = d.title;
      q("[data-reelv-name]").href = d.url;
      q("[data-reelv-link]").href = d.url;
      q("[data-reelv-thumb]").src = d.thumb || "";
      q("[data-reelv-thumb]").hidden = !d.thumb;
      q("[data-reelv-price]").textContent = d.price;
      q("[data-reelv-was]").textContent = d.was || "";
      q("[data-reelv-badge]").textContent = d.off ? `${d.off}% off` : "";
      q("[data-reelv-badge]").hidden = !d.off;
      const btn = q("[data-reelv-add]");
      btn.disabled = !d.available;
      btn.textContent = !d.available ? "Sold out" : d.multi ? "Choose size" : "Add to cart";
    };

    root.addEventListener("click", e => {
      const o = e.target.closest("[data-reel-open]");
      if (!o) return;
      // the player opens silent too, unless this reel's sound was already on in the row
      muted = $("[data-reel-sound]", o.closest(".reel__frame"))?.getAttribute("aria-pressed") !== "true";
      video.muted = true;
      previews.forEach(v => { v.pause(); setRowSound(v, false, true); });
      show(+o.dataset.reelOpen);
      openLayer(viewer, "[data-reelv-step='1']");
    });

    // stop the video whenever the player closes (close button, Escape, or the backdrop)
    new MutationObserver(() => {
      if (!viewer.classList.contains("is-open")) { video.pause(); clearIg(); }
    }).observe(viewer, { attributes: true, attributeFilter: ["class"] });

    $$("[data-reelv-step]", viewer).forEach(b => b.addEventListener("click", () => show(i + +b.dataset.reelvStep)));
    muteBtn.addEventListener("click", () => { muted = !muted; syncMute(); });
    q("[data-reelv-share]").addEventListener("click", async () => {
      const d = data[i];
      const url = location.origin + (d.url || location.pathname);
      try {
        if (navigator.share) await navigator.share({ title: d.title || document.title, url });
        else { await navigator.clipboard.writeText(url); toast("Link copied"); }
      } catch { /* share sheet dismissed */ }
    });
    q("[data-reelv-add]").addEventListener("click", e => {
      const d = data[i];
      if (d.multi) openQuickView(d.handle);
      else add(e.currentTarget, d.variant);
    });

    document.addEventListener("keydown", e => {
      if (!viewer.classList.contains("is-open")) return;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); show(i + 1); }
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); show(i - 1); }
    });
    let y0 = null;
    const stage = q(".reelv__stage");
    stage.addEventListener("touchstart", e => { y0 = e.touches[0].clientY; }, { passive: true });
    stage.addEventListener("touchend", e => {
      if (y0 === null) return;
      const dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dy) > 50) show(i + (dy < 0 ? 1 : -1));
      y0 = null;
    });
  });

  /* ---------- predictive search ---------- */

  (() => {
    const input = $("#searchInput"), out = $("#searchResults");
    if (!input || !out) return;
    let timer, ctrl;
    input.addEventListener("input", () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (!q) { out.innerHTML = ""; return; }
      timer = setTimeout(async () => {
        ctrl?.abort();
        ctrl = new AbortController();
        try {
          const url = `${RF.routes.predictiveSearch}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8`;
          const data = await (await fetch(url, { signal: ctrl.signal })).json();
          const items = data.resources?.results?.products || [];
          out.innerHTML = items.length
            ? items.map(p => `
                <li><a href="${esc(p.url)}">
                  <span class="sr-thumb">${p.image ? `<img src="${esc(imgUrl(p.image, 120))}" alt="" loading="lazy">` : ""}</span>
                  <span><span class="sr-name">${esc(p.title)}</span><br><span class="sr-meta">${esc(p.type || "")}${p.type ? " · " : ""}${formatMoney(Math.round(parseFloat(p.price) * 100))}</span></span>
                </a></li>`).join("") +
              `<li class="search__all"><a href="${RF.routes.search}?q=${encodeURIComponent(q)}&type=product">View all results →</a></li>`
            : `<li class="search__empty">No fragrances match “${esc(q)}”.</li>`;
        } catch (err) { if (err.name !== "AbortError") out.innerHTML = ""; }
      }, 220);
    });
  })();

  /* ---------- hero slideshow ---------- */

  $$("[data-hero-root]").forEach(hero => {
    const slides = $$(".slide", hero);
    const dots = $("[data-hero-dots]", hero);
    if (slides.length < 2 || !dots) return;
    const interval = (+dots.dataset.interval || 6) * 1000;
    hero.style.setProperty("--hero-interval", interval + "ms");
    let i = 0, timer;
    dots.innerHTML = slides.map((_, n) => `<button class="hero__dot" type="button" role="tab" aria-label="Slide ${n + 1}"></button>`).join("");
    const dotEls = $$(".hero__dot", dots);
    const restart = () => {
      clearTimeout(timer);
      if (!reduceMotion && !hero.classList.contains("is-paused")) timer = setTimeout(() => go(i + 1), interval);
    };
    function go(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach((s, k) => { s.classList.toggle("is-active", k === i); s.inert = k !== i; });
      dotEls.forEach((d, k) => { d.classList.remove("is-active"); d.setAttribute("aria-selected", k === i); });
      void dots.offsetWidth;
      dotEls[i].classList.add("is-active");
      restart();
    }
    $$("[data-hero]", hero).forEach(b => b.addEventListener("click", () => go(i + +b.dataset.hero)));
    dotEls.forEach((d, k) => d.addEventListener("click", () => go(k)));
    const pause = on => { hero.classList.toggle("is-paused", on); on ? clearTimeout(timer) : restart(); };
    hero.addEventListener("mouseenter", () => pause(true));
    hero.addEventListener("mouseleave", () => pause(false));
    hero.addEventListener("focusin", () => pause(true));
    hero.addEventListener("focusout", () => pause(false));
    let x0 = null;
    hero.addEventListener("touchstart", e => { x0 = e.touches[0].clientX; }, { passive: true });
    hero.addEventListener("touchend", e => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 50) go(i + (dx < 0 ? 1 : -1));
      x0 = null;
    });
    go(0);
  });

  /* ---------- compare duo ---------- */

  $$("[data-duo]").forEach(duo => {
    const range = $(".duo__range", duo);
    const set = v => { v = Math.max(4, Math.min(96, v)); duo.style.setProperty("--pos", v + "%"); range.value = v; };
    range.addEventListener("input", () => set(+range.value));
    let dragging = false;
    const fromEvent = e => { const r = duo.getBoundingClientRect(); set((e.clientX - r.left) / r.width * 100); };
    duo.addEventListener("pointerdown", e => {
      if (e.target.closest("a, button")) return;
      dragging = true;
      duo.setPointerCapture(e.pointerId);
      fromEvent(e);
    });
    duo.addEventListener("pointermove", e => { if (dragging) fromEvent(e); });
    duo.addEventListener("pointerup", () => { dragging = false; });
    duo.addEventListener("pointercancel", () => { dragging = false; });
  });

  /* ---------- carousels ---------- */

  function initCarousels(root = document) {
    $$("[data-carousel]:not([data-ready])", root).forEach(c => {
      c.setAttribute("data-ready", "");
      const track = $(".carousel__track", c);
      const prev = $(".carousel__arrow--prev", c), next = $(".carousel__arrow--next", c);
      const update = () => {
        prev.disabled = track.scrollLeft < 4;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      };
      prev.addEventListener("click", () => track.scrollBy({ left: -track.clientWidth * .8 }));
      next.addEventListener("click", () => track.scrollBy({ left: track.clientWidth * .8 }));
      track.addEventListener("scroll", update, { passive: true });
      addEventListener("resize", update);
      update();
    });
  }
  initCarousels();

  /* ---------- product page ---------- */

  let syncVariant = null;
  // offer pills with a discount code copy it when tapped
  $$("[data-copy-code]").forEach(b => b.addEventListener("click", async () => {
    const code = b.dataset.copyCode;
    try { await navigator.clipboard.writeText(code); toast(`Code ${code} copied`); }
    catch { toast(`Use code ${code} at checkout`); }
  }));

  const pdp = $("[data-product-root]");
  if (pdp) {
    // gallery
    const stage = $("#pdpStage");
    const imgs = $$(".pdp__img", stage);
    let view = 0;
    const show = k => {
      if (!imgs.length) return;
      view = (k + imgs.length) % imgs.length;
      imgs.forEach((im, n) => im.classList.toggle("is-off", n !== view));
      $$("[data-view]", pdp).forEach(t => {
        const on = +t.dataset.view === view;
        t.classList.toggle("is-active", on);
        t.setAttribute("aria-selected", on);
      });
    };
    $$("[data-view]", pdp).forEach(b => b.addEventListener("click", () => show(+b.dataset.view)));
    $$("[data-step-view]", pdp).forEach(b => b.addEventListener("click", () => show(view + +b.dataset.stepView)));
    // on phones the stage is a native scroll strip (see CSS), so the tap-to-swap swipe stays off
    const strip = matchMedia("(max-width:720px)");
    let sx = null;
    stage?.addEventListener("touchstart", e => { sx = strip.matches ? null : e.touches[0].clientX; }, { passive: true });
    stage?.addEventListener("touchend", e => {
      if (sx === null || imgs.length < 2) return;
      const dx = e.changedTouches[0].clientX - sx;
      if (Math.abs(dx) > 40) show(view + (dx < 0 ? 1 : -1));
      sx = null;
    });

    // variant picker
    const json = $("[data-product-json]", pdp);
    const product = json ? JSON.parse(json.textContent) : null;
    const form = $("#product-form");
    const idInput = form && $("[data-variant-id]", form);
    const addBtn = form && $("[data-add-button]", form);
    syncVariant = () => {
      if (!product || !form) return;
      const sel = product.options.map((_, i) => $(`input[data-option="${i + 1}"]:checked`, form)?.value);
      const v = sel.every(Boolean)
        ? product.variants.find(x => x.options.every((o, i) => o === sel[i]))
        : product.variants.find(x => String(x.id) === idInput.value);
      $$(".sizes .size", form).forEach(l => l.classList.toggle("is-checked", $("input", l).checked));
      if (!v) { addBtn.disabled = true; addBtn.textContent = "Unavailable"; return; }
      idInput.value = v.id;
      addBtn.disabled = !v.available;
      addBtn.textContent = v.available ? "Add to cart" : "Sold out";
      const buyNow = $("[data-buy-now]", form);
      if (buyNow) buyNow.disabled = !v.available;
      const priceHtml = (v.compare_at_price > v.price ? `<s class="price__was">${formatMoney(v.compare_at_price)}</s> ` : "") + `<span class="price__now">${formatMoney(v.price)}</span>`;
      $$("[data-price]", pdp).forEach(p => p.innerHTML = priceHtml);
      const sticky = $("[data-sticky-add]", pdp);
      if (sticky) { sticky.disabled = !v.available; sticky.textContent = v.available ? "Add to cart" : "Sold out"; }
      if (v.featured_media) {
        const k = imgs.findIndex(im => (im.currentSrc || im.src).includes(String(v.featured_media.preview_image?.src || "").split("?")[0].split("/").pop()));
        if (k >= 0) show(k);
      }
      const url = new URL(location.href);
      url.searchParams.set("variant", v.id);
      history.replaceState({}, "", url);
    };
    form?.addEventListener("change", e => { if (e.target.matches("[data-option]")) syncVariant(); });
    syncVariant();

    // quantity
    const qtyInput = form && $("[data-qty-input]", form);
    $$("[data-qty-step]", form || pdp).forEach(b => b.addEventListener("click", () => {
      qtyInput.value = Math.max(1, Math.min(10, (+qtyInput.value || 1) + +b.dataset.qtyStep));
    }));

    // sticky add bar
    const sticky = $("#stickyBuy");
    const buy = $("#pdpBuy");
    if (sticky && buy && "IntersectionObserver" in window) {
      new IntersectionObserver(([e]) => {
        const on = !e.isIntersecting && e.boundingClientRect.top < 0;
        sticky.classList.toggle("is-on", on);
        sticky.setAttribute("aria-hidden", !on);
        $("[data-sticky-add]", sticky).tabIndex = on ? 0 : -1;
      }).observe(buy);
      $("[data-sticky-add]", sticky)?.addEventListener("click", () => form?.requestSubmit());
    }
  }

  // recommendations load after the page
  $$("[data-recommendations]").forEach(async el => {
    try {
      const html = await (await fetch(el.dataset.url)).text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const fresh = doc.querySelector("[data-recommendations]");
      if (fresh && fresh.innerHTML.trim()) { el.innerHTML = fresh.innerHTML; initCarousels(el); }
      else el.remove();
    } catch { el.remove(); }
  });

  /* ---------- collection filters ---------- */

  const colForm = $("[data-collection-form]");
  if (colForm) {
    const submit = () => {
      const params = new URLSearchParams(new FormData(colForm));
      for (const [k, v] of [...params]) if (v === "") params.delete(k);
      location.href = `${colForm.action}?${params}`;
    };
    colForm.addEventListener("change", e => {
      // on phones, wait for "Show results" so several filters can be picked
      if (matchMedia("(max-width: 960px)").matches && e.target.closest("#filters")) return;
      submit();
    });
    colForm.addEventListener("submit", e => { e.preventDefault(); submit(); });
  }
  $("#filterBtn")?.addEventListener("click", () => openLayer($("#filters")));

  /* ---------- misc ---------- */

  // FAQ search
  $$("[data-faq]").forEach(root => {
    const input = $("#faqSearch", root);
    if (!input) return;
    input.addEventListener("input", () => {
      const q = input.value.trim().toLowerCase();
      let shown = 0;
      $$("details", root).forEach(d => {
        const hit = !q || d.textContent.toLowerCase().includes(q);
        d.hidden = !hit;
        if (hit) shown++;
        if (q && hit) d.open = true;
      });
      $$(".faq__group", root).forEach(g => g.hidden = !$$("details:not([hidden])", g).length);
      $("#faqEmpty", root).hidden = shown > 0;
    });
  });

  // login page: switch to password recovery
  $$("[data-toggle-recover]").forEach(a => a.addEventListener("click", e => {
    e.preventDefault();
    $("#login").hidden = !$("#login").hidden;
    $("#recover").hidden = !$("#recover").hidden;
  }));
  if (location.hash === "#recover" && $("#recover")) { $("#login").hidden = true; $("#recover").hidden = false; }

  // booking date can't be in the past
  $$("[data-min-today]").forEach(i => i.min = new Date().toISOString().slice(0, 10));

  // keep size chips' checked style in sync on any page
  document.addEventListener("change", e => {
    if (e.target.matches(".size input")) $$(".size", e.target.closest(".sizes")).forEach(l => l.classList.toggle("is-checked", $("input", l).checked));
  });
  $$(".size input:checked").forEach(i => i.closest(".size").classList.add("is-checked"));

  RF.template = document.body.dataset.template;
  if (RF.template !== "cart") refreshCart();
})();
